import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessagesSquare, Plus, ArrowLeft, Flag, MessageCircle, Send, UserX, ShieldOff } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "../components/ui/dialog";
import { SecureAccountCard } from "../components/SecureAccountCard";

export default function Forum() {
  const [boards, setBoards] = useState([]);
  const [active, setActive] = useState(null);
  const [threads, setThreads] = useState([]);
  const [openThread, setOpenThread] = useState(null);

  useEffect(() => { api.get("/forum/boards").then(({ data }) => setBoards(data.boards)).catch(() => {}); }, []);

  const openBoard = async (b) => {
    setActive(b);
    try { const { data } = await api.get("/forum/threads", { params: { board: b.key } }); setThreads(data.threads); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const refresh = () => active && openBoard(active);

  if (openThread) return <ThreadView tid={openThread} onBack={() => { setOpenThread(null); refresh(); }} />;

  return (
    <div className="space-y-8">
      <SecureAccountCard />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-head text-3xl font-bold text-[#2C363F] sm:text-4xl">Community</h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">A moderated, anonymous-friendly space to share and support one another. Be kind — everyone here is on their own journey.</p>
        </div>
        <BlockedManager />
      </div>

      {!active ? (
        <div className="grid gap-5 sm:grid-cols-2">
          {boards.map((b) => (
            <button key={b.key} onClick={() => openBoard(b)} data-testid={`board-${b.key}`}
              className="rounded-2xl border border-border bg-white p-6 text-left shadow-sm transition-transform duration-300 hover:-translate-y-1">
              <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-[#8FB8DE]/20 text-[#2C4763]"><MessagesSquare strokeWidth={1.8} className="h-5 w-5" /></div>
              <h3 className="font-head text-lg font-semibold text-[#2C363F]">{b.name}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{b.desc}</p>
              <span className="mt-3 inline-block text-xs font-medium text-[#4A7c59]">{b.thread_count} {b.thread_count === 1 ? "conversation" : "conversations"}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => setActive(null)} data-testid="back-boards"><ArrowLeft className="mr-2 h-4 w-4" /> All boards</Button>
            <NewThread board={active.key} onDone={refresh} />
          </div>
          <div><h2 className="font-head text-2xl font-bold text-[#2C363F]">{active.name}</h2><p className="text-sm text-muted-foreground">{active.desc}</p></div>
          <div className="space-y-3" data-testid="threads-list">
            {threads.map((t) => (
              <button key={t.id} onClick={() => setOpenThread(t.id)} data-testid={`thread-${t.id}`}
                className="block w-full rounded-2xl border border-border bg-white p-5 text-left shadow-sm transition-transform duration-300 hover:-translate-y-0.5">
                <h3 className="font-head font-semibold text-[#2C363F]">{t.title}</h3>
                <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{t.body}</p>
                <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground"><span>{t.author_name}</span><span className="flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5" /> {t.reply_count}</span></div>
              </button>
            ))}
            {threads.length === 0 && <p className="py-10 text-center text-muted-foreground">No conversations yet — start the first one.</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function NewThread({ board, onDone }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const submit = async () => {
    if (!title.trim() || !body.trim()) { toast.error("Add a title and message."); return; }
    try { await api.post("/forum/threads", { board, title, body }); toast.success("Posted. Thanks for sharing."); setOpen(false); setTitle(""); setBody(""); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="new-thread-btn" className="rounded-full bg-[#4A7c59] hover:bg-[#3B6347]"><Plus className="mr-2 h-4 w-4" /> New post</Button></DialogTrigger>
      <DialogContent className="max-w-lg rounded-2xl">
        <DialogHeader><DialogTitle className="font-head">Start a conversation</DialogTitle><DialogDescription>Share what's on your mind — you can post anonymously.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input data-testid="thread-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 rounded-xl" /></div>
          <div><Label>Your message</Label><Textarea data-testid="thread-body" value={body} onChange={(e) => setBody(e.target.value)} className="mt-1 min-h-32 rounded-xl" /></div>
          <Button data-testid="thread-submit" onClick={submit} className="w-full rounded-full bg-[#4A7c59] hover:bg-[#3B6347]">Post</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BlockedManager() {
  const [open, setOpen] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const load = () => api.get("/forum/blocks").then(({ data }) => setBlocks(data.blocks)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);
  const unblock = async (id) => {
    try { await api.delete(`/forum/block/${id}`); toast.success("Unblocked."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="manage-blocked-btn" variant="outline" className="rounded-full border-border"><ShieldOff className="mr-2 h-4 w-4" /> Blocked members</Button></DialogTrigger>
      <DialogContent className="max-w-md rounded-2xl">
        <DialogHeader><DialogTitle className="font-head">Blocked members</DialogTitle><DialogDescription>You won't see posts or replies from anyone you've blocked.</DialogDescription></DialogHeader>
        <div className="space-y-2" data-testid="blocked-list">
          {blocks.map((b) => (
            <div key={b.id} className="flex items-center justify-between rounded-xl border border-border p-3">
              <span className="text-sm text-[#2C363F]">{b.blocked_name}</span>
              <Button data-testid={`unblock-${b.blocked_id}`} onClick={() => unblock(b.blocked_id)} variant="outline" size="sm" className="rounded-full">Unblock</Button>
            </div>
          ))}
          {blocks.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">You haven't blocked anyone.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}


function ThreadView({ tid, onBack }) {
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const load = () => api.get(`/forum/threads/${tid}`).then(({ data }) => setData(data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, [tid]);

  const send = async () => {
    if (!reply.trim()) return;
    try { await api.post(`/forum/threads/${tid}/reply`, { body: reply }); setReply(""); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const report = async (kind, id) => {
    try { await api.post("/forum/report", { reason: "" }, { params: { kind, item_id: id } }); toast.success("Reported to moderators. Thank you for keeping this space safe."); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const block = async (authorId, name) => {
    try { await api.post("/forum/block", { user_id: authorId, name }); toast.success(`You won't see posts from ${name || "this member"} anymore.`); onBack(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!data) return <div className="h-40 animate-pulse rounded-2xl bg-muted" />;
  return (
    <div className="space-y-5">
      <Button variant="ghost" onClick={onBack} data-testid="back-thread"><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>
      <div className="rounded-2xl border border-border bg-white p-6 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <h1 className="font-head text-2xl font-bold text-[#2C363F]">{data.thread.title}</h1>
          <div className="flex shrink-0 gap-2">
            <button onClick={() => report("thread", data.thread.id)} title="Report" data-testid="report-thread" className="text-muted-foreground hover:text-[#8a4436]"><Flag className="h-4 w-4" /></button>
            <button onClick={() => block(data.thread.author_id, data.thread.author_name)} title="Block this member" data-testid="block-thread" className="text-muted-foreground hover:text-[#8a4436]"><UserX className="h-4 w-4" /></button>
          </div>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{data.thread.author_name}</p>
        <p className="mt-3 whitespace-pre-wrap text-[#2C363F]">{data.thread.body}</p>
      </div>

      <div className="space-y-3" data-testid="replies-list">
        {data.replies.map((r) => (
          <div key={r.id} className="rounded-2xl border border-border bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <p className="text-xs font-medium text-muted-foreground">{r.author_name}</p>
              <div className="flex gap-2">
                <button onClick={() => report("reply", r.id)} title="Report" data-testid={`report-${r.id}`} className="text-muted-foreground hover:text-[#8a4436]"><Flag className="h-3.5 w-3.5" /></button>
                <button onClick={() => block(r.author_id, r.author_name)} title="Block this member" data-testid={`block-${r.id}`} className="text-muted-foreground hover:text-[#8a4436]"><UserX className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <p className="mt-1 whitespace-pre-wrap text-sm text-[#2C363F]">{r.body}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-3 rounded-2xl border border-border bg-white p-4 shadow-sm">
        <Textarea data-testid="reply-body" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a supportive reply…" className="min-h-12 rounded-xl" />
        <Button data-testid="reply-submit" onClick={send} className="rounded-full bg-[#4A7c59] hover:bg-[#3B6347]"><Send className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}
